import React, { useState, useEffect } from "react";
import { Menu, X, Leaf, Phone } from "lucide-react";
import { Button } from "../components/ui/button";
import { SITE } from "../data/content";

const NAV_LINKS = [
  { label: "Services", href: "#services" },
  { label: "Why Us", href: "#why-us" },
  { label: "Pricing", href: "#pricing" },
  { label: "Projects", href: "#projects" },
  { label: "Areas", href: "#service-area" },
  { label: "Contact", href: "#quote" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (href) => {
    setOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-white/80 border-b border-border shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between h-20">
          <button
            data-testid="logo-button"
            onClick={() => scrollTo("#home")}
            className="flex items-center gap-2 group"
          >
            <span className="flex items-center justify-center w-10 h-10 rounded-sm bg-primary text-primary-foreground">
              <Leaf className="w-5 h-5" />
            </span>
            <span className={`font-semibold tracking-tight text-lg leading-tight text-left ${scrolled ? "text-foreground" : "text-white"}`}>
              Prairie Cleanout
              <span className="block text-xs font-normal opacity-70">{SITE.location}</span>
            </span>
          </button>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <button
                key={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => scrollTo(l.href)}
                className={`text-sm font-medium transition-colors hover:text-accent ${
                  scrolled ? "text-foreground/80" : "text-white/90"
                }`}
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a
              data-testid="nav-call-button"
              href={`tel:${SITE.contacts[0].tel}`}
              className={`flex items-center gap-2 text-sm font-semibold transition-colors ${scrolled ? "text-foreground hover:text-accent" : "text-white hover:text-secondary"}`}
            >
              <Phone className="w-4 h-4" /> {SITE.contacts[0].phone}
            </a>
            <Button
              data-testid="nav-quote-button"
              onClick={() => scrollTo("#quote")}
              className="rounded-sm bg-accent text-accent-foreground hover:bg-primary transition-colors"
            >
              Get a Free Quote
            </Button>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className={`lg:hidden ${scrolled ? "text-foreground" : "text-white"}`}
            onClick={() => setOpen(!open)}
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white border-b border-border px-6 py-4 flex flex-col gap-3">
          {NAV_LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => scrollTo(l.href)}
              className="text-left text-base font-medium text-foreground/80 py-1"
            >
              {l.label}
            </button>
          ))}
          <Button
            data-testid="mobile-quote-button"
            onClick={() => scrollTo("#quote")}
            className="rounded-sm bg-accent text-accent-foreground mt-2"
          >
            Get a Free Quote
          </Button>
          <div className="flex flex-col gap-2 pt-2 border-t border-border mt-2">
            {SITE.contacts.map((c) => (
              <a
                key={c.tel}
                data-testid={`mobile-call-${c.name.toLowerCase()}`}
                href={`tel:${c.tel}`}
                className="flex items-center gap-2 text-sm font-semibold text-foreground"
              >
                <Phone className="w-4 h-4 text-accent" /> {c.name}: {c.phone}
              </a>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};
